/**
 * Per-template catalog labels.
 *
 * The underlying API table is `properties` (named for villa-rental V1) but
 * the dashboard renames "Properties" depending on the active vertical
 * template so a restaurant operator doesn't see a sidebar item that says
 * "Properties" when they're really browsing their restaurant locations or
 * menu sections.
 *
 * Source of truth for the active slug is `business_settings.current_template_slug`,
 * which the Templates page writes via POST /api/templates/apply.
 */

import {
  Building2,
  Utensils,
  Sparkles,
  Smile,
  Coffee,
  Car,
  type LucideIcon,
} from "lucide-react";

export interface CatalogLabel {
  plural: string;
  singular: string;
  icon: LucideIcon;
  description: string;
  /**
   * Suffix shown after the price in the catalog list.
   *   "/mo"        long-term rentals
   *   ""           one-time services (dentist, spa session)
   *   " min spend" private events / catering tiers with floor price
   *   " /person"   per-head pricing (catering box, table prix-fixe)
   */
  priceSuffix: string;
  /** Optional override: e.g. "Capacity" instead of "Bedrooms". */
  capacityLabel: string;
}

export const DEFAULT_CATALOG_LABEL: CatalogLabel = {
  plural: "Catalog",
  singular: "Item",
  icon: Building2,
  description: "Items the bot can reference.",
  priceSuffix: "",
  capacityLabel: "Capacity",
};

const BY_SLUG: Record<string, CatalogLabel> = {
  "villa-rental": {
    plural: "Properties",
    singular: "Property",
    icon: Building2,
    description: "Villa rentals available for long-term lease.",
    priceSuffix: " /mo",
    capacityLabel: "BR",
  },
  restaurant: {
    plural: "Restaurants",
    singular: "Restaurant",
    icon: Utensils,
    description: "Locations the bot can take reservations for.",
    priceSuffix: " min spend",
    capacityLabel: "Seats",
  },
  spa: {
    plural: "Treatments",
    singular: "Treatment",
    icon: Sparkles,
    description: "Treatments and packages the bot can book.",
    priceSuffix: "",
    capacityLabel: "Min",
  },
  dentist: {
    plural: "Services",
    singular: "Service",
    icon: Smile,
    description: "Treatments and procedures the bot can book.",
    priceSuffix: "",
    capacityLabel: "Min",
  },
  cafe: {
    plural: "Menu & Catering",
    singular: "Item",
    icon: Coffee,
    description: "Catering tiers and menu sections the bot can reference.",
    priceSuffix: " /person",
    capacityLabel: "Max",
  },
  automobile: {
    plural: "Vehicles",
    singular: "Vehicle",
    icon: Car,
    description: "Demo vehicle examples the bot can reference (availability not confirmed).",
    priceSuffix: "",
    capacityLabel: "Year",
  },
};

export function catalogLabelForSlug(
  slug: string | null | undefined,
): CatalogLabel {
  if (!slug) return DEFAULT_CATALOG_LABEL;
  return BY_SLUG[slug] ?? DEFAULT_CATALOG_LABEL;
}
